import { useAppDispatch, useAppSelector } from '@app/store/store';
import { Switch } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback, useState } from 'react';

import { useRecordingAnalytics } from '../../../hooks';
import { selectAspectRatio, selectDraftClips } from '../../../selector';
import { PresetSelector } from '../../color-presets/preset-selector';
import { CaptionOptions } from '../../icons/caption-options';
import { Palette } from '../../icons/palette';
import { ActiveWordColorControls } from './active-word-color-controls';
import { CaptionPositionControls } from './caption-position-controls';
import { FontStyleSelector } from './font-style-selector';
import { VideoBackgroundControls } from './video-background-controls';

type AppearanceProps = {
  disabled?: boolean;
  /** When true, panel content cannot scroll (e.g. when overlay is shown in editor). */
  scrollLocked?: boolean;
};

export const Appearance = ({
  disabled = false,
  scrollLocked = false,
}: AppearanceProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const aspectRatio = useAppSelector(selectAspectRatio);
  const draftClips = useAppSelector(selectDraftClips);
  const { enabled: captionsEnabled } = useAppSelector(
    state => state.contentHub.recordingInfo.captions,
  );
  const [isFontListExpanded, setIsFontListExpanded] = useState(false);
  const isPortrait = aspectRatio === '9_16';
  const hasClips = draftClips.length > 0;
  // No clips + landscape: show caption switch off (deselected), but clickable to create full clip on turn on
  const captionsSwitchChecked =
    !hasClips && !isPortrait ? false : captionsEnabled;

  const handleCaptionsToggle = useCallback(
    (enabled: boolean) => {
      // When enabling captions with no clips (landscape), create full-length clip first
      if (enabled && !hasClips && !isPortrait) {
        dispatch({ type: '[recording] create full clip for landscape' });
      }
      dispatch({
        type: '[recording] captions enabled changed',
        payload: { enabled },
      });
      trackRecordingEvent('ch_creator_lab_appearance_tab_captions_toggle', {
        toggle: enabled ? 'ON' : 'OFF',
      });
    },
    [dispatch, hasClips, isPortrait, trackRecordingEvent],
  );

  // When font list is expanded, only show the FontStyleSelector
  const overflowClass =
    scrollLocked || disabled ? 'overflow-hidden' : 'overflow-y-auto';

  if (isFontListExpanded) {
    return (
      <div
        className={cx(
          'flex flex-col h-full px-4 pt-5 [scrollbar-width:thin] [scrollbar-color:#696e7c_transparent]',
          overflowClass,
          disabled && 'pointer-events-none opacity-60',
        )}
        {...(disabled && { 'aria-disabled': true })}
      >
        <FontStyleSelector
          isExpanded={true}
          onExpandChange={setIsFontListExpanded}
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <div
      className={cx(
        'flex flex-col h-full px-4 [scrollbar-width:thin] [scrollbar-color:#696e7c_transparent]',
        overflowClass,
        disabled && 'pointer-events-none opacity-60',
      )}
      {...(disabled && { 'aria-disabled': true })}
    >
      <div className="flex items-center gap-4 pb-2 pt-1">
        <Palette className="size-5 text-white shrink-0" />
        <h2 className="text-[17px] font-inter font-medium text-white leading-5 m-0">
          {t('content-recording.appearance.header')}
        </h2>
      </div>

      <div className="flex flex-col gap-4 mt-3 mb-3">
        <div className="flex flex-col gap-3">
          <p className="text-[14px] font-inter font-medium text-white leading-5 m-0">
            {t('content-recording.appearance.brand.label')}
          </p>
          <p className="text-[12px] font-inter font-normal text-spring-neutral-b3 leading-[18px] m-0">
            {t('content-recording.appearance.brand.description')}
          </p>
          <PresetSelector disabled={disabled} />
        </div>
        {/* Video background (solid/blur): portrait only */}
        {isPortrait && <VideoBackgroundControls disabled={disabled} />}
      </div>

      {/* Captions Toggle: no clips + landscape = show off, clickable (creates full clip on turn on); no clips + portrait = disabled; has clips = normal */}
      <div className="flex items-center gap-4 my-5">
        <CaptionOptions className="size-5 shrink-0 text-white" />
        <p className="flex-1 text-[17px] font-inter font-medium text-white leading-[25px] tracking-[-0.2px] m-0">
          {t('content-recording.appearance.captions.label')}
        </p>
        <Switch
          checked={captionsSwitchChecked}
          onChange={handleCaptionsToggle}
          disabled={disabled || (!hasClips && isPortrait)}
          className={cx(
            'relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 disabled:cursor-not-allowed disabled:opacity-60',
            captionsSwitchChecked
              ? 'bg-spring-primary'
              : 'bg-spring-neutral-b3',
          )}
        >
          <span className="sr-only">
            {t('content-recording.appearance.captions.label')}
          </span>
          <span
            aria-hidden="true"
            className={cx(
              'pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
              captionsSwitchChecked ? 'translate-x-3' : 'translate-x-0',
            )}
          />
        </Switch>
      </div>

      {/* Caption Controls - only show when switch is on (matches displayed state) */}
      {captionsSwitchChecked && (
        <div className="flex flex-col gap-4">
          <CaptionPositionControls disabled={disabled} />
          <FontStyleSelector
            isExpanded={false}
            onExpandChange={setIsFontListExpanded}
            disabled={disabled}
          />
          <ActiveWordColorControls disabled={disabled} />
        </div>
      )}
    </div>
  );
};
